import React, { useState } from 'react'
import { motion } from 'framer-motion'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  DocumentTextIcon,
  PlusIcon,
  PencilIcon,
  TrashIcon,
  MagnifyingGlassIcon,
  FunnelIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  PlayIcon,
} from '@heroicons/react/24/outline'

import { apiClient } from '../../lib/api'
import LoadingSpinner from '../ui/LoadingSpinner'

interface ResponseTemplate {
  id: string
  name: string
  description?: string
  category: string
  template_text: string
  template_content?: string  // Keep for backwards compatibility
  variables?: Record<string, any>
  conditions?: Record<string, any>
  is_active?: boolean
  is_default?: boolean
  priority?: number
  created_at: string
  updated_at?: string
}

interface TemplateCategory {
  key: string
  label: string
  count: number
}

const ResponseTemplates: React.FC = () => {
  const [selectedCategory, setSelectedCategory] = useState<string>('all')
  const [searchTerm, setSearchTerm] = useState('')
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [editingTemplate, setEditingTemplate] = useState<ResponseTemplate | null>(null)
  const [showTestModal, setShowTestModal] = useState(false)
  const [testingTemplate, setTestingTemplate] = useState<ResponseTemplate | null>(null)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [deletingTemplate, setDeletingTemplate] = useState<ResponseTemplate | null>(null)

  const queryClient = useQueryClient()

  const { data: templatesData, isLoading } = useQuery({
    queryKey: ['response-templates'],
    queryFn: () => {
      console.log('=== RML DEBUG === Fetching response templates')
      return apiClient.get('/intelligence/templates')
    },
    onSuccess: (data) => {
      console.log('=== RML DEBUG === Templates loaded successfully:', data)
    },
    onError: (error) => {
      console.error('=== RML DEBUG === Templates loading failed:', error)
    }
  })

  const { data: categoriesData } = useQuery({
    queryKey: ['template-categories'],
    queryFn: () => apiClient.get('/intelligence/templates/categories')
  })

  const createTemplateMutation = useMutation({
    mutationFn: (data: Partial<ResponseTemplate>) =>
      apiClient.post('/intelligence/templates', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['response-templates'] })
      setShowCreateModal(false)
    }
  })

  const updateTemplateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<ResponseTemplate> }) =>
      apiClient.put(`/intelligence/templates/${id}`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['response-templates'] })
      setEditingTemplate(null)
    }
  })

  const deleteTemplateMutation = useMutation({
    mutationFn: (id: string) => apiClient.delete(`/intelligence/templates/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['response-templates'] })
    }
  })

  const testTemplateMutation = useMutation({
    mutationFn: (data: { template_content: string; test_variables: Record<string, string> }) =>
      apiClient.post('/intelligence/templates/test', data)
  })

  const allTemplates = templatesData?.data || []
  const categories = categoriesData?.data || []

  // Client-side filtering
  const templates = allTemplates.filter((template: ResponseTemplate) => {
    // Category filter
    const matchesCategory = selectedCategory === 'all' || template.category === selectedCategory

    // Search filter
    const matchesSearch = !searchTerm ||
      template.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (template.template_text || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (template.description || '').toLowerCase().includes(searchTerm.toLowerCase())

    return matchesCategory && matchesSearch
  })

  const handleDeleteTemplate = (template: ResponseTemplate) => {
    setDeletingTemplate(template)
    setShowDeleteModal(true)
  }

  const confirmDeleteTemplate = () => {
    if (deletingTemplate) {
      deleteTemplateMutation.mutate(deletingTemplate.id)
      setShowDeleteModal(false)
      setDeletingTemplate(null)
    }
  }

  const handleTestTemplate = (template: ResponseTemplate) => {
    setTestingTemplate(template)
    setShowTestModal(true)

    // Run test with sample variables
    const sampleVariables = {
      user_name: 'John',
      issue_description: 'login problems',
      company_name: 'Your Company',
      support_agent: 'Sarah'
    }

    testTemplateMutation.mutate({
      template_content: template.template_text || template.template_content || '',
      test_variables: sampleVariables
    })
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <DocumentTextIcon className="w-8 h-8 text-indigo-600" />
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Response Templates</h2>
            <p className="text-gray-600">Manage crisis response and communication templates</p>
          </div>
        </div>

        <button
          onClick={() => {
            console.log('=== RML DEBUG === Create Template button clicked')
            setShowCreateModal(true)
            console.log('=== RML DEBUG === showCreateModal set to true')
          }}
          className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700"
        >
          <PlusIcon className="w-4 h-4 mr-2" />
          New Template
        </button>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-3 sm:space-y-0 sm:space-x-4">
          {/* Search */}
          <div className="relative flex-1 max-w-md">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search templates..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
            />
          </div>

          {/* Category Filter */}
          <div className="flex items-center space-x-2">
            <FunnelIcon className="w-4 h-4 text-gray-400" />
            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              className="border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
            >
              <option value="all">All Categories ({allTemplates.length})</option>
              {categories.map((category: TemplateCategory) => (
                <option key={category.key} value={category.key}>
                  {category.label} ({category.count})
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Templates Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {templates.map((template: ResponseTemplate) => (
          <motion.div
            key={template.id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-lg shadow-sm border border-gray-200 p-6"
          >
            <div className="flex items-start justify-between mb-3">
              <div className="flex-1">
                <div className="flex items-center space-x-2 mb-1">
                  <h3 className="text-lg font-semibold text-gray-900">{template.name}</h3>
                  {template.is_default && (
                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-indigo-100 text-indigo-800">
                      ⭐ System Template
                    </span>
                  )}
                  {template.is_active === false && (
                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-800">
                      Inactive
                    </span>
                  )}
                </div>
                {template.description && (
                  <p className="text-sm text-gray-600 mb-2">{template.description}</p>
                )}
                <div className="flex items-center space-x-4 text-xs text-gray-500">
                  <span>Category: {template.category}</span>
                  {template.priority && <span>Priority: {template.priority}</span>}
                </div>
              </div>
            </div>

            {/* Template Preview */}
            <div className="bg-gray-50 rounded-md p-3 mb-4">
              <p className="text-sm text-gray-700 line-clamp-3">
                {(template.template_text || template.template_content || '').substring(0, 150)}...
              </p>
            </div>

            {/* Variables */}
            {template.variables && Object.keys(template.variables).length > 0 && (
              <div className="mb-4">
                <p className="text-xs font-medium text-gray-700 mb-1">Variables:</p>
                <div className="flex flex-wrap gap-1">
                  {Object.keys(template.variables).map((variable) => (
                    <span
                      key={variable}
                      className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-indigo-100 text-indigo-800"
                    >
                      {variable}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Actions */}
            <div className="flex items-center justify-between pt-3 border-t border-gray-200">
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => handleTestTemplate(template)}
                  className="inline-flex items-center px-2 py-1 border border-gray-300 rounded text-xs font-medium text-gray-700 bg-white hover:bg-gray-50"
                >
                  <PlayIcon className="w-3 h-3 mr-1" />
                  Test
                </button>
                <button
                  onClick={() => setEditingTemplate(template)}
                  className="inline-flex items-center px-2 py-1 border border-gray-300 rounded text-xs font-medium text-gray-700 bg-white hover:bg-gray-50"
                >
                  <PencilIcon className="w-3 h-3 mr-1" />
                  Edit
                </button>
              </div>

              {!template.is_default && (
                <button
                  onClick={() => handleDeleteTemplate(template)}
                  className="inline-flex items-center px-2 py-1 border border-red-300 rounded text-xs font-medium text-red-700 bg-white hover:bg-red-50"
                >
                  <TrashIcon className="w-3 h-3 mr-1" />
                  Delete
                </button>
              )}
            </div>
          </motion.div>
        ))}
      </div>

      {/* Empty State */}
      {templates.length === 0 && (
        <div className="text-center py-12">
          <DocumentTextIcon className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No templates found</h3>
          <p className="text-gray-600 mb-4">
            {searchTerm || selectedCategory !== 'all'
              ? 'Try adjusting your search or filter criteria.'
              : 'Create your first response template to get started.'}
          </p>
          <button
            onClick={() => setShowCreateModal(true)}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700"
          >
            <PlusIcon className="w-4 h-4 mr-2" />
            Create Template
          </button>
        </div>
      )}

      {/* Test Results Modal */}
      {showTestModal && testingTemplate && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75" onClick={() => setShowTestModal(false)} />

            <div className="inline-block w-full max-w-2xl my-8 overflow-hidden text-left align-middle transition-all transform bg-white shadow-xl rounded-lg">
              <div className="px-6 py-4 border-b border-gray-200">
                <h3 className="text-lg font-medium text-gray-900">Template Test Results</h3>
                <p className="text-sm text-gray-600">Testing: {testingTemplate.name}</p>
              </div>

              <div className="px-6 py-4 space-y-4">
                {testTemplateMutation.isPending && (
                  <div className="flex items-center justify-center py-8">
                    <LoadingSpinner />
                  </div>
                )}

                {testTemplateMutation.data && (
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Original Template</label>
                      <div className="bg-gray-50 rounded-md p-3">
                        <pre className="text-sm text-gray-700 whitespace-pre-wrap">
                          {testTemplateMutation.data.data.original_template}
                        </pre>
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Processed Output</label>
                      <div className="bg-green-50 rounded-md p-3 border border-green-200">
                        <pre className="text-sm text-gray-700 whitespace-pre-wrap">
                          {testTemplateMutation.data.data.processed_content}
                        </pre>
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Test Variables Used</label>
                      <div className="bg-blue-50 rounded-md p-3">
                        <pre className="text-sm text-gray-700">
                          {JSON.stringify(testTemplateMutation.data.data.test_variables, null, 2)}
                        </pre>
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Quality Metrics</label>
                      <div className="grid grid-cols-2 gap-4">
                        <div className="bg-white border border-gray-200 rounded-md p-3">
                          <div className="text-sm font-medium text-gray-900">Readability</div>
                          <div className="text-lg font-bold text-green-600">
                            {testTemplateMutation.data.data.metrics.readability_score}%
                          </div>
                        </div>
                        <div className="bg-white border border-gray-200 rounded-md p-3">
                          <div className="text-sm font-medium text-gray-900">Sentiment</div>
                          <div className="text-lg font-bold text-blue-600">
                            {testTemplateMutation.data.data.metrics.sentiment_score}%
                          </div>
                        </div>
                        <div className="bg-white border border-gray-200 rounded-md p-3">
                          <div className="text-sm font-medium text-gray-900">Professionalism</div>
                          <div className="text-lg font-bold text-purple-600">
                            {testTemplateMutation.data.data.metrics.professionalism_score}%
                          </div>
                        </div>
                        <div className="bg-white border border-gray-200 rounded-md p-3">
                          <div className="text-sm font-medium text-gray-900">Length</div>
                          <div className="text-lg font-bold text-gray-600">
                            {testTemplateMutation.data.data.metrics.processed_length} chars
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {testTemplateMutation.error && (
                  <div className="bg-red-50 border border-red-200 rounded-md p-4">
                    <div className="flex items-center">
                      <ExclamationTriangleIcon className="w-5 h-5 text-red-400 mr-2" />
                      <p className="text-sm text-red-700">
                        Failed to test template. Please try again.
                      </p>
                    </div>
                  </div>
                )}
              </div>

              <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex justify-end">
                <button
                  onClick={() => setShowTestModal(false)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Create Template Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75" onClick={() => {
              console.log('=== RML DEBUG === Create modal backdrop clicked, closing modal')
              setShowCreateModal(false)
            }} />

            <div className="inline-block w-full max-w-2xl my-8 overflow-hidden text-left align-middle transition-all transform bg-white shadow-xl rounded-lg">
              <div className="px-6 py-4 border-b border-gray-200">
                <h3 className="text-lg font-medium text-gray-900">Create New Response Template</h3>
                <p className="text-sm text-gray-600">Design a professional crisis response template</p>
              </div>

              <form onSubmit={(e) => {
                e.preventDefault()
                console.log('=== RML DEBUG === Create template form submitted')

                const formData = new FormData(e.currentTarget)
                const templateData = {
                  name: formData.get('name') as string,
                  description: formData.get('description') as string,
                  category: formData.get('category') as string,
                  template_content: formData.get('template_content') as string,
                  variables: [],
                  conditions: {},
                  is_active: true,
                  priority: 50
                }

                console.log('=== RML DEBUG === Creating template with data:', templateData)
                createTemplateMutation.mutate(templateData)
              }}>
                <div className="px-6 py-4 space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Template Name</label>
                    <input
                      type="text"
                      name="name"
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                      placeholder="e.g., Customer Complaint Response"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
                    <input
                      type="text"
                      name="description"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                      placeholder="Brief description of when to use this template"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Category</label>
                    <select
                      name="category"
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                    >
                      <option value="de_escalation">De-escalation</option>
                      <option value="professional">Professional Response</option>
                      <option value="collaborative">Collaborative Problem-Solving</option>
                      <option value="transparent">Transparent Communication</option>
                      <option value="apology">Apology & Acknowledgment</option>
                      <option value="solution_focused">Solution-Focused</option>
                      <option value="empathetic">Empathetic Response</option>
                      <option value="general">General</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Template Content</label>
                    <textarea
                      name="template_content"
                      required
                      rows={8}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                      placeholder="Write your response template here. Use {variable_name} for dynamic content."
                    />
                  </div>

                  <div className="bg-blue-50 rounded-md p-3">
                    <p className="text-sm text-blue-700">
                      <strong>Tip:</strong> Use variables like {'{user_name}'}, {'{issue_description}'}, {'{company_name}'} to make your template dynamic.
                    </p>
                  </div>
                </div>

                <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex justify-end space-x-3">
                  <button
                    type="button"
                    onClick={() => {
                      console.log('=== RML DEBUG === Create modal cancel clicked')
                      setShowCreateModal(false)
                    }}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={createTemplateMutation.isPending}
                    className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 border border-transparent rounded-md hover:bg-indigo-700 disabled:opacity-50"
                  >
                    {createTemplateMutation.isPending ? 'Creating...' : 'Create Template'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Edit Template Modal */}
      {editingTemplate && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75" onClick={() => setEditingTemplate(null)} />

            <div className="inline-block w-full max-w-2xl my-8 overflow-hidden text-left align-middle transition-all transform bg-white shadow-xl rounded-lg">
              <div className="px-6 py-4 border-b border-gray-200">
                <h3 className="text-lg font-medium text-gray-900">Edit Template</h3>
                <p className="text-sm text-gray-600">Update your response template</p>
              </div>

              <form onSubmit={(e) => {
                e.preventDefault()
                console.log('=== RML DEBUG === Edit template form submitted')

                const formData = new FormData(e.currentTarget)
                const templateData = {
                  name: formData.get('name') as string,
                  description: formData.get('description') as string,
                  category: formData.get('category') as string,
                  template_content: formData.get('template_content') as string,
                }

                console.log('=== RML DEBUG === Updating template with data:', templateData)
                updateTemplateMutation.mutate({ id: editingTemplate.id, data: templateData })
              }}>
                <div className="px-6 py-4 space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Template Name</label>
                    <input
                      type="text"
                      name="name"
                      required
                      defaultValue={editingTemplate.name}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
                    <input
                      type="text"
                      name="description"
                      defaultValue={editingTemplate.description || ''}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                      placeholder="Brief description of when to use this template"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Category</label>
                    <select
                      name="category"
                      required
                      defaultValue={editingTemplate.category}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                    >
                      <option value="de_escalation">De-escalation</option>
                      <option value="professional">Professional Response</option>
                      <option value="collaborative">Collaborative Problem-Solving</option>
                      <option value="transparent">Transparent Communication</option>
                      <option value="apology">Apology & Acknowledgment</option>
                      <option value="solution_focused">Solution-Focused</option>
                      <option value="empathetic">Empathetic Response</option>
                      <option value="general">General</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Template Content</label>
                    <textarea
                      name="template_content"
                      required
                      rows={8}
                      defaultValue={editingTemplate.template_text || editingTemplate.template_content || ''}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                      placeholder="Write your response template here. Use {variable_name} for dynamic content."
                    />
                  </div>
                </div>

                <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex justify-end space-x-3">
                  <button
                    type="button"
                    onClick={() => setEditingTemplate(null)}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={updateTemplateMutation.isPending}
                    className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 border border-transparent rounded-md hover:bg-indigo-700 disabled:opacity-50"
                  >
                    {updateTemplateMutation.isPending ? 'Updating...' : 'Update Template'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && deletingTemplate && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75" onClick={() => setShowDeleteModal(false)} />

            <div className="inline-block w-full max-w-md my-8 overflow-hidden text-left align-middle transition-all transform bg-white shadow-xl rounded-lg">
              <div className="px-6 py-4">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <ExclamationTriangleIcon className="w-6 h-6 text-red-600" />
                  </div>
                  <div className="ml-3">
                    <h3 className="text-lg font-medium text-gray-900">Delete Template</h3>
                    <div className="mt-2">
                      <p className="text-sm text-gray-500">
                        Are you sure you want to delete "<strong>{deletingTemplate.name}</strong>"? This action cannot be undone.
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => setShowDeleteModal(false)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={confirmDeleteTemplate}
                  disabled={deleteTemplateMutation.isPending}
                  className="px-4 py-2 text-sm font-medium text-white bg-red-600 border border-transparent rounded-md hover:bg-red-700 disabled:opacity-50"
                >
                  {deleteTemplateMutation.isPending ? 'Deleting...' : 'Delete Template'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Success Messages */}
      {createTemplateMutation.isSuccess && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-green-50 border border-green-200 rounded-md p-4"
        >
          <div className="flex items-center">
            <CheckCircleIcon className="w-5 h-5 text-green-400 mr-2" />
            <p className="text-sm text-green-700">Template created successfully!</p>
          </div>
        </motion.div>
      )}

      {updateTemplateMutation.isSuccess && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-green-50 border border-green-200 rounded-md p-4"
        >
          <div className="flex items-center">
            <CheckCircleIcon className="w-5 h-5 text-green-400 mr-2" />
            <p className="text-sm text-green-700">Template updated successfully!</p>
          </div>
        </motion.div>
      )}

      {/* Debug Info */}
      {process.env.NODE_ENV === 'development' && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-md p-4 mt-4">
          <p className="text-sm text-yellow-700">
            <strong>Debug Info:</strong> showCreateModal = {showCreateModal.toString()}, templates loaded = {templates.length}
          </p>
        </div>
      )}
    </div>
  )
}

export default ResponseTemplates